import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  CreditCard,
  Banknote,
  Repeat,
  CheckCircle,
  XCircle,
  ShoppingCart,
  RefreshCw,
  AlertCircle,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";

// Configuración de la conexión al backend
const API_BASE_URL = "https://supersofia.nvirred.com/consulta.php";

const PointOfSalePage = () => {
  const { pointId } = useParams();
  const [pointName, setPointName] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Estados para auto-refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [cashClosureAlert, setCashClosureAlert] = useState(null);
  const [previousPaymentsCount, setPreviousPaymentsCount] = useState(0);

  // Referencias para intervalos
  const refreshIntervalRef = useRef(null);
  const alertTimeoutRef = useRef(null);

  // Función para obtener los métodos de pago desde el backend
  const fetchPaymentMethods = async () => {
    try {
      console.log("🔍 Cargando métodos de pago...");
      const response = await fetch(`${API_BASE_URL}?route=payment-methods`);
      if (!response.ok) throw new Error("Error al cargar métodos de pago");
      const data = await response.json();
      console.log("✅ Métodos de pago cargados:", data);
      setPaymentMethods(data);
      if (data.length > 0) {
        setPaymentMethod(data[0].name);
      }
    } catch (err) {
      console.error("❌ Error al cargar los métodos de pago:", err);
      setError("Error al cargar métodos de pago. Revisa tu backend.");
    }
  };

  // Función para obtener información del punto de venta
  const fetchPointData = async () => {
    try {
      console.log("🔍 Cargando datos del punto de venta ID:", pointId);

      const response = await fetch(`${API_BASE_URL}?route=payment-points`);
      if (!response.ok) throw new Error("Error al cargar puntos de venta");
      const points = await response.json();

      const currentPoint = points.find((point) => point.id == pointId);
      if (!currentPoint) {
        throw new Error("Punto de venta no encontrado");
      }

      console.log("✅ Punto de venta encontrado:", currentPoint);
      setPointName(currentPoint.name);
    } catch (err) {
      console.error("❌ Error al cargar datos del punto:", err);
      setError("Error al cargar los datos del punto de venta.");
    }
  };

  // Función para obtener las ventas/cobros del día actual
  const fetchTodaySales = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setIsRefreshing(true);

      console.log("🔍 Cargando ventas del día para el punto:", pointId);

      const response = await fetch(
        `${API_BASE_URL}?route=payment-points/${pointId}/today-sales`
      );
      if (!response.ok) throw new Error("Error al cargar ventas del día");
      const data = await response.json();

      console.log("✅ Ventas del día cargadas:", data);

      // Detectar si hubo un cierre de caja (reducción significativa en el número de pagos)
      if (
        previousPaymentsCount > 0 &&
        data.length < previousPaymentsCount * 0.5
      ) {
        console.log("🚨 Posible cierre de caja detectado!");
        setCashClosureAlert({
          message: "¡El administrador realizó un cierre de caja!",
          previousCount: previousPaymentsCount,
          currentCount: data.length,
          timestamp: new Date(),
        });

        // Limpiar la alerta después de 10 segundos
        if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
        alertTimeoutRef.current = setTimeout(() => {
          setCashClosureAlert(null);
        }, 10000);
      }

      setPreviousPaymentsCount(data.length);
      setPayments(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("❌ Error al cargar ventas:", err);
      setPayments([]);
    } finally {
      if (showRefreshIndicator) setIsRefreshing(false);
    }
  };

  // Configurar auto-refresh
  useEffect(() => {
    // Configurar intervalo para auto-refresh cada 30 segundos
    refreshIntervalRef.current = setInterval(() => {
      fetchTodaySales(true);
    }, 30000); // 30 segundos

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, [pointId]);

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      setError(null);

      try {
        await Promise.all([
          fetchPointData(),
          fetchPaymentMethods(),
          fetchTodaySales(),
        ]);
      } catch (err) {
        console.error("❌ Error al inicializar página:", err);
      } finally {
        setLoading(false);
      }
    };

    if (pointId) {
      initializePage();
    }
  }, [pointId]);

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setSubmitting(true);

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setSubmitStatus("error");
      setSubmitting(false);
      alert("Por favor, ingresa un monto válido y positivo.");
      return;
    }

    if (!paymentMethod) {
      setSubmitStatus("error");
      setSubmitting(false);
      alert("Por favor, selecciona un método de pago.");
      return;
    }

    try {
      console.log("💰 Registrando cobro:", {
        payment_point_id: parseInt(pointId),
        amount: parsedAmount,
        payment_method: paymentMethod,
      });

      const response = await fetch(`${API_BASE_URL}?route=sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          payment_point_id: parseInt(pointId),
          amount: parsedAmount,
          payment_method: paymentMethod,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Error al registrar el cobro");
      }

      const result = await response.json();
      console.log("✅ Respuesta del servidor:", result);

      if (result.success) {
        // Recargar las ventas del día para mostrar la nueva venta
        await fetchTodaySales();

        // Limpiar el formulario
        setAmount("");
        setSubmitStatus("success");

        console.log("✅ Cobro registrado exitosamente en la base de datos");

        setTimeout(() => setSubmitStatus(null), 3000);
      } else {
        throw new Error(result.error || "Error en la respuesta del servidor");
      }
    } catch (error) {
      console.error("❌ Error al registrar el cobro:", error);
      setSubmitStatus("error");
      alert(`Hubo un error al registrar el cobro: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Función para refresh manual
  const handleManualRefresh = () => {
    fetchTodaySales(true);
  };

  const getMethodIcon = (methodName) => {
    switch (methodName) {
      case "mercadopago":
        return <ShoppingCart className="w-4 h-4 text-blue-500" />;
      case "transferencia":
        return <Repeat className="w-4 h-4 text-purple-500" />;
      case "tarjeta_credito":
        return <CreditCard className="w-4 h-4 text-orange-500" />;
      case "efectivo":
        return <Banknote className="w-4 h-4 text-green-500" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatMethodName = (methodName) => {
    const method = paymentMethods.find((m) => m.name === methodName);
    return method
      ? method.display_name
      : methodName
          .replace("_", " ")
          .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">
            Cargando punto de venta...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-50 to-pink-50">
        <div className="text-center max-w-sm">
          <XCircle className="w-16 h-16 text-red-500 mb-4 mx-auto" />
          <p className="text-red-600 text-lg font-semibold mb-2">¡Error!</p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm hover:bg-blue-700 transition-colors"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Header compacto */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2">
        {/* Alerta de cierre de caja */}
        <AnimatePresence>
          {cashClosureAlert && (
            <motion.div
              className="mb-3 p-3 bg-orange-100 border border-orange-300 rounded-xl shadow-sm"
              initial={{ opacity: 0, y: -30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-orange-800 text-sm leading-tight">
                    {cashClosureAlert.message}
                  </p>
                  <p className="text-xs text-orange-700">
                    Continúa registrando nuevos cobros
                  </p>
                </div>
                <button
                  onClick={() => setCashClosureAlert(null)}
                  className="text-orange-600 hover:text-orange-800 p-1"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          className="text-center"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {pointName}
            </span>
          </h1>
        </motion.div>
      </div>

      {/* Contenido principal con scroll */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Formulario de registro */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Registrar Cobro
            </h2>
            <form onSubmit={handleAddPayment} className="space-y-4">
              {/* Input de monto */}
              <div>
                <label htmlFor="amount" className="block text-gray-700 text-sm font-medium mb-1">
                  Monto
                </label>
                <input
                  type="number"
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-medium"
                  placeholder="0.00"
                  step="0.01"
                  required
                  disabled={submitting}
                />
              </div>

              {/* Métodos de pago - Grid compacto */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Método de Pago
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethods.map((method) => (
                    <motion.button
                      key={method.id}
                      type="button"
                      onClick={() => setPaymentMethod(method.name)}
                      disabled={submitting}
                      className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                        paymentMethod === method.name
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      } ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
                      whileHover={!submitting ? { scale: 1.02 } : {}}
                      whileTap={!submitting ? { scale: 0.98 } : {}}
                    >
                      {getMethodIcon(method.name)}
                      <span className="truncate">{method.display_name}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Botón de submit */}
              <motion.button
                type="submit"
                disabled={submitting}
                className={`w-full bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                  submitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
                whileHover={!submitting ? { scale: 1.02 } : {}}
                whileTap={!submitting ? { scale: 0.98 } : {}}
              >
                <DollarSign className="w-5 h-5" />
                {submitting ? "Registrando..." : "Registrar Cobro"}
              </motion.button>

              {/* Status messages */}
              <AnimatePresence>
                {submitStatus === "success" && (
                  <motion.div
                    className="p-3 bg-green-100 text-green-700 rounded-xl flex items-center gap-2 text-sm"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <CheckCircle className="w-4 h-4" />
                    ¡Cobro registrado con éxito!
                  </motion.div>
                )}
                {submitStatus === "error" && (
                  <motion.div
                    className="p-3 bg-red-100 text-red-700 rounded-xl flex items-center gap-2 text-sm"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <XCircle className="w-4 h-4" />
                    Error al registrar el cobro
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </motion.div>

          {/* Lista de cobros */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Cobros de Hoy
              </h2>
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className={`flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors text-xs ${
                  isRefreshing ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <RefreshCw className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`} />
                {isRefreshing ? "..." : "Actualizar"}
              </button>
            </div>

            {payments.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-600 text-sm mb-1">
                  Aún no hay cobros registrados
                </p>
                <p className="text-xs text-gray-500">
                  Se actualiza cada 30 segundos
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Total del día */}
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-blue-700 font-semibold">
                    Total: $
                    {payments
                      .reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
                      .toFixed(2)}
                  </p>
                  <p className="text-blue-600 text-xs">
                    {payments.length} cobro{payments.length !== 1 ? "s" : ""}
                  </p>
                </div>

                {/* Lista de cobros - máximo 5 visibles con scroll */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <AnimatePresence>
                    {payments.slice(0, 10).map((payment, index) => (
                      <motion.div
                        key={payment.id}
                        className="bg-gray-50 rounded-xl p-3 flex justify-between items-center border border-gray-100"
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 20, opacity: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {getMethodIcon(payment.payment_method)}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 text-sm truncate">
                              {formatMethodName(payment.payment_method)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(payment.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm font-bold text-green-700 flex-shrink-0">
                          ${parseFloat(payment.amount).toFixed(2)}
                        </p>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {payments.length > 10 && (
                  <p className="text-xs text-gray-500 text-center">
                    Mostrando los últimos 10 cobros
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default PointOfSalePage;